import React, { ChangeEvent, useCallback, useState } from 'react';
import {
  Button,
  Grid,
  useTheme,
  InputAdornment,
  IconButton,
  Typography,
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { MdModeEdit } from 'react-icons/md';
import { IoIosArrowDropleftCircle } from 'react-icons/io';
import { toast, ToastContainer } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/auth';

import MaterialInput from '../../components/inputs/MaterialInput';
import api from '../../services/api';

import avatarImg from '../../assets/avatarimage.png';
import { useStyles } from './styles';
import 'react-toastify/dist/ReactToastify.css';

interface PasswordFormData {
  password: string;
  new_password: string;
}

const Profile: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { t } = useTranslation();
  const { jwt, user, updateUserAvatar } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(
    false,
  );

  const schema = Yup.object().shape({
    password: Yup.string().required(
      t('components:forms.currentPasswordRequired'),
    ),
    new_password: Yup.string(),
    password_confirmation: Yup.string().oneOf(
      [Yup.ref('new_password')],
      t('components:forms.passwordsMatchError'),
    ),
  });

  const handleGetToken = useCallback(
    async (password: string): Promise<string> => {
      try {
        const response = await api.post('login', {
          email: user.email,
          password,
        });
        const token = response.data.jwt;

        return token;
      } catch (err) {
        toast.error(t('components:toast.error.wrongPassword'));
        return '';
      }
    },
    [user.email, t],
  );

  const handleChangePassword = useCallback(
    async (data: PasswordFormData) => {
      if (data.password === data.new_password) {
        toast.error(t('components:toast.error.newPasswordSameAsOld'));
        return;
      }

      const newToken = await handleGetToken(data.password);

      if (newToken) {
        try {
          const formData = {
            userId: user.id,
            password: data.new_password,
          };

          await api
            .put('user/password/change', formData, {
              headers: {
                'x-access-token': newToken,
              },
            })
            .then(() => {
              toast.success(t('components:toast.success.passwordUpdated'));
            });
        } catch (err) {
          toast.error(t('components:toast.error.passwordUpdateError'));
        }
      }
    },
    [user, handleGetToken, t],
  );

  const handleAvatarChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const data = new FormData();
        data.append('avatar', e.target.files[0]);
        data.append('userId', user.id);

        api
          .post('user/avatar', data, {
            headers: {
              'x-access-token': jwt,
              'Content-Type': 'multipart/form-data',
            },
          })
          .then(apiReponse => {
            updateUserAvatar(apiReponse.data, user);
          })
          .then(() => {
            toast.success(t('components:toast.success.avatarUpdated'));
          });
      }
    },
    [jwt, updateUserAvatar, user, t],
  );

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): any => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      name: user.name.split(' ')[0],
      lastname: user.name.split(' ')[1],
      email: user.email,
      password: '',
      new_password: '',
      password_confirmation: '',
    },
    validationSchema: schema,
    onSubmit: (values, { resetForm }) => {
      const data = {
        password: values.password,
        new_password: values.new_password,
      };

      handleChangePassword(data);
      resetForm();
    },
  });

  return (
    <div className={classes.container}>
      <Grid container spacing={3} style={{ flexDirection: 'row-reverse' }}>
        <Grid item xs={12} sm={4} className={classes.avatarContainer}>
          <div className={classes.avatarInput}>
            <img
              src={user.avatar_url ? user.avatar_url : avatarImg}
              alt={user.name}
              className={classes.avatarImage}
            />
            <label htmlFor="avatar" className={classes.avatarLabel}>
              <MdModeEdit size={20} color={theme.palette.common.white} />

              <input
                type="file"
                id="avatar"
                className={classes.avatarButton}
                onChange={handleAvatarChange}
              />
            </label>
          </div>
        </Grid>
        <Grid item xs={12} sm={8} className={classes.content}>
          <form onSubmit={formik.handleSubmit} className={classes.form}>
            <div className={classes.floatingButton}>
              <button
                type="button"
                onClick={() => history.goBack()}
                className={classes.backButton}
              >
                <IoIosArrowDropleftCircle size={30} color="#7030A0" />
              </button>
              <div>
                <Typography variant="h3" className={classes.title}>
                  {t('screens:profile.updateProfile')}
                </Typography>
              </div>
            </div>
            <div className={classes.nameContainer}>
              <MaterialInput
                label={t('components:forms.name')}
                disabled
                id="username"
                name="name"
                className={classes.input}
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
              />
              <MaterialInput
                disabled
                id="lastname"
                name="lastname"
                label={t('components:forms.lastname')}
                className={classes.input}
                value={formik.values.lastname}
                onChange={formik.handleChange}
                error={
                  formik.touched.lastname && Boolean(formik.errors.lastname)
                }
              />
            </div>
            <MaterialInput
              disabled
              id="email"
              name="email"
              label={t('components:forms.email')}
              className={classes.input}
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              width="100%"
            />
            <MaterialInput
              id="password"
              name="password"
              label={t('components:forms.currentPassword')}
              className={classes.input}
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t(
                      'components:forms.togglePasswordVisibilityAria',
                    )}
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? (
                      <FaEye color="#9DA5B1" />
                    ) : (
                      <FaEyeSlash color="#9DA5B1" />
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />
            <MaterialInput
              id="new_password"
              name="new_password"
              label={t('components:forms.newPassword')}
              className={classes.input}
              type={showNewPassword ? 'text' : 'password'}
              value={formik.values.new_password}
              onChange={formik.handleChange}
              error={
                formik.touched.new_password &&
                Boolean(formik.errors.new_password)
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t(
                      'components:forms.toggleNewPasswordVisibilityAria',
                    )}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showNewPassword ? (
                      <FaEye color="#9DA5B1" />
                    ) : (
                      <FaEyeSlash color="#9DA5B1" />
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />
            <MaterialInput
              id="password_confirmation"
              name="password_confirmation"
              label={t('components:forms.passwordConfirmation')}
              className={classes.input}
              type={showPasswordConfirmation ? 'text' : 'password'}
              value={formik.values.password_confirmation}
              onChange={formik.handleChange}
              error={
                formik.touched.password_confirmation &&
                Boolean(formik.errors.password_confirmation)
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t(
                      'components:forms.toggleNewPasswordConfirmationVisibilityAria',
                    )}
                    onClick={() =>
                      setShowPasswordConfirmation(!showPasswordConfirmation)
                    }
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPasswordConfirmation ? (
                      <FaEye color="#9DA5B1" />
                    ) : (
                      <FaEyeSlash color="#9DA5B1" />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              labelWidth={0}
            />
            <div className={classes.buttonsContainer}>
              <Button
                variant="outlined"
                color="primary"
                className={classes.cancelButton}
              >
                {t('components:buttons.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => formik.handleSubmit()}
                className={classes.saveButton}
              >
                {t('components:buttons.save')}
              </Button>
            </div>
          </form>
        </Grid>
      </Grid>
      <ToastContainer style={{ position: 'absolute', top: 64 }} />
    </div>
  );
};

export default Profile;
